import { z } from "zod";
import { Version } from "./concurrent.js";
import { Invoice } from "./invoice.js";

// ---------------------------------------------------------------------------
// 領収書（#39、031、docs/19 §7「請求書の上に載る。小さい」）。
//   出した請求書 → 領収書を起こす（明細・丸めを写す。合計は請求書と同じ）→ 領収日・但し書きだけ直す
//   → 出す（R-0001）→ PDF。第1版は 1請求書＝1領収書（分割入金の複数領収書は 入金 #38 と一緒に）
// ---------------------------------------------------------------------------

export const RECEIPT_MSG = {
  notFound: "その領収書は見つかりません",
  notDraft: "出したあとの領収書は書き換えられません。修正するときは取消しで下書きに戻します",
  invoiceNotIssued: "出したあとの請求書からだけ領収書を起こせます",
  draftExists: "この請求書には書きかけの領収書があります。そちらをひらきます",
  alreadyExists: "この請求書の領収書はもう出ています",
  companyRequired: "会社名を入れてください。領収書の発行者として印字されます",
  notIssued: "出したあとの領収書だけ取り消せます",
  unissueDisabled: "出した領収書の取消しは、設定で止めています",
  /** 下書きの紙を見る（#149：見積書と同じ文言） */
  pdfDraft: "PDF で確認（透かし入り）",
} as const;

/**
 * 領収書。請求書（Invoice）と同じ形に、元の請求書への結びを足したもの。
 * 明細・内訳・税は請求書から写した値（画面では直せない。直すなら請求書側を直して起こし直す）
 */
export const Receipt = Invoice.extend({
  /** どの請求書の領収書か（document_links converts_from）。入金から起こした領収書は documentNo が null（請求書は明細の行に出る） */
  fromInvoice: z.object({ id: z.string().uuid(), documentNo: z.string().nullable() }),
  /** どの入金の領収書か（E1。documents.payment_id）。入金と結ばれていなければ null */
  fromPayment: z.object({ id: z.string().uuid() }).nullable().optional(),
});
export type Receipt = z.infer<typeof Receipt>;

/**
 * 起こす：出した請求書から（fromInvoiceId）か、入金から（fromPaymentId、E1、営業力 docs/08 決めごと11）のどちらか1つ。
 * recordPayment（決めごと12。sales の会社だけ効く。既定 true）：請求書から起こすとき、今日・残の全額・現金 の入金も記録する
 */
export const ReceiptCreate = z
  .object({
    fromInvoiceId: z.string().uuid().optional(),
    fromPaymentId: z.string().uuid().optional(),
    recordPayment: z.boolean().default(true),
  })
  .refine((x) => !!x.fromInvoiceId !== !!x.fromPaymentId, { message: "どの請求書（または入金）から起こすかを1つだけ指定します" });
export type ReceiptCreate = z.infer<typeof ReceiptCreate>;

/** 下書きの保存。直せるのは領収日と但し書きだけ（明細は請求書の写し） */
export const ReceiptSave = z.object({
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** 但し書き（「防犯カメラ設置工事 代金として」）。空にもできる */
  notes: z.string().trim().max(200).optional(),
  /** 版（#202）：画面が読んだときの Receipt.updatedAt。渡すと、いまの版と違えば 409 RECEIPT_STALE（領収書の画面は必ず送る） */
  version: Version.optional(),
});
export type ReceiptSave = z.infer<typeof ReceiptSave>;
